import { useEffect, useMemo } from 'react'
import { Navigate, Outlet, useMatches, useParams } from 'react-router-dom'

import { AppShell } from '@/components/layout/AppShell'
import { VenueExposureBanner } from '@/components/venue/VenueExposureBanner'
import { VenueForbiddenNotice } from '@/components/venue/VenueForbiddenNotice'
import { useManagedVenues } from '@/hooks/useManagedVenues'
import { localManagedVenueSource, parseVenueId } from '@/lib/venue/managedVenue'
import { findManagedVenue } from '@/lib/venue/managerVenueTypes'
import { useVenueStore } from '@/stores/venueStore'

import { VenueContext } from './venueContext'

interface RouteHandle {
  suppressExposureBanner?: boolean
}

/** 자체 상태 요약이 있는 화면(대시보드)은 셸 레벨 배너를 억제한다(설계 §2 자율 결정 D2). */
function useSuppressExposureBanner(): boolean {
  const matches = useMatches()
  return matches.some(
    (match) => (match.handle as RouteHandle | undefined)?.suppressExposureBanner === true
  )
}

/**
 * 구장 하위 화면 공통 레이아웃 (설계 §2-2(2) · §2-1(d)).
 *
 * 책임 4가지:
 *  1. `:venueId` 파라미터 형식 검증 — 양의 정수가 아니면 구장 선택으로 돌린다.
 *  2. **내 구장 컨텍스트 조회** — 헤더에 띄울 구장명을 서버값으로 채운다.
 *  3. 목록에 없는 구장이면 접근 불가 안내(403 공통 처리의 선제 안내).
 *  4. 셸로 감싸 `<Outlet/>` 렌더 + 하위 화면이 쓸 구장 컨텍스트 제공.
 *
 * 🚨 **소속 검사는 fail-open 이다**(설계 D6). 목록 조회가 로딩·실패 중이면 화면을 막지 않는다.
 * 실제 방어선은 서버의 행 단위 403(#584 §2)이고, 여기서 fail-closed 를 쓰면 일시적 네트워크
 * 오류가 정상 사용자를 잠근다.
 */
export function VenueLayout() {
  const params = useParams()
  const venueId = parseVenueId(params.venueId)
  const contextValue = useMemo(() => (venueId === null ? null : { venueId }), [venueId])
  const suppressExposureBanner = useSuppressExposureBanner()

  const venuesQuery = useManagedVenues()
  const managedVenue = venueId === null ? undefined : findManagedVenue(venuesQuery.data, venueId)
  // 목록을 **성공적으로 받았는데** 그 구장이 없을 때만 차단한다(fail-open).
  const isNotManaged = venuesQuery.isSuccess && managedVenue === undefined

  const setCurrentVenue = useVenueStore((state) => state.setCurrentVenue)
  const clearCurrentVenue = useVenueStore((state) => state.clearCurrentVenue)

  // 재로그인·재진입 시 마지막 구장으로 돌아오기 위한 편의값. 딥링크로 들어와도 기록된다.
  // 🚨 더 이상 관리하지 않는 구장이면 잊는다 — 안 그러면 다음 로그인에서 또 이 화면으로 온다(R6).
  useEffect(() => {
    if (venueId === null) return
    if (isNotManaged) {
      localManagedVenueSource.forget()
      return
    }
    localManagedVenueSource.remember(venueId)
  }, [isNotManaged, venueId])

  // 헤더 구장명은 서버값이 정본이다. 조회 전이면 셸이 `구장 #id` 폴백을 쓴다(R5).
  useEffect(() => {
    if (venueId === null) return
    if (managedVenue) {
      setCurrentVenue({ venueId, venueName: managedVenue.name })
      return
    }
    clearCurrentVenue()
  }, [clearCurrentVenue, managedVenue, setCurrentVenue, venueId])

  if (venueId === null || contextValue === null) {
    return <Navigate to="/venues/select" replace />
  }

  return (
    <VenueContext.Provider value={contextValue}>
      <AppShell venueId={venueId}>
        {isNotManaged ? (
          <VenueForbiddenNotice />
        ) : (
          <>
            {suppressExposureBanner ? null : <VenueExposureBanner venueId={venueId} />}
            <Outlet />
          </>
        )}
      </AppShell>
    </VenueContext.Provider>
  )
}
